import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Layers, Plus, Trash2, Edit2, Search } from 'lucide-react';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { SETORES_COMPONENT_DESIGN } from './SetoresComponent.design';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';

// hooks
import useAuth from 'src/hooks/useAuth';

// actions
import v1_actionListaSetores from 'src/actions/v1/clientes/v1_actionListaSetores';
import v2_actionCriarSetor from 'src/actions/v2/cliente/setores/v2_actionCriarSetor';
import v2_actionEditarSetor from 'src/actions/v2/cliente/setores/v2_actionEditarSetor';
import v2_actionDeletarSetor from 'src/actions/v2/cliente/setores/v2_actionDeletarSetor';

const TABLE_HEAD = [
  { id: 'nome', label: 'Nome do Setor', className: 'flex-1' },
  { id: 'descricao', label: 'Descrição', className: 'flex-1 hidden md:block' },
  { id: 'actions', label: 'Ações', className: 'w-24 text-right' },
];

export default function SetoresComponent() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // Data State
  const [listaSetores, setListaSetores] = useState([]);
  const [loading, setLoading] = useState(false);

  // UI State
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [action, setAction] = useState('Criar');

  // Form State
  const [setorId, setSetorId] = useState('');
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');

  useEffect(() => {
    buscarListaSetores();
  }, []);

  async function buscarListaSetores() {
    try {
      const response = await v1_actionListaSetores(user.clienteId);
      if (response.status === 200) {
        setListaSetores(response.data.data);
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Erro ao buscar setores', { variant: 'error' });
    }
  }

  const handleOpenDialog = (actionType = 'Criar', setor = null) => {
    setAction(actionType);
    if (setor && actionType === 'Editar') {
      setSetorId(setor._id);
      setNome(setor.nome_setor);
      setDescricao(setor.description || '');
    } else {
      setSetorId('');
      setNome('');
      setDescricao('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSetorId('');
    setNome('');
    setDescricao('');
  };

  const handleSave = async () => {
    if (!nome) return;

    setLoading(true);
    try {
      const data = {
        scope: {
          name: nome,
          description: descricao
        }
      };

      if (action === 'Criar') {
        await v2_actionCriarSetor(user.clienteId, data);
        enqueueSnackbar('Setor criado com sucesso!', { variant: 'success' });
      } else {
        await v2_actionEditarSetor(setorId, data);
        enqueueSnackbar('Setor atualizado com sucesso!', { variant: 'success' });
      }

      await buscarListaSetores();
      handleCloseDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao salvar setor', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSetorId(id);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const data = { checkUsers: true };
      const response = await v2_actionDeletarSetor(setorId, data);

      if (response.status === 200) {
        if (response.data.error) {
          enqueueSnackbar(response.data.message, { variant: 'error' });
        } else {
          enqueueSnackbar('Setor excluído com sucesso!', { variant: 'success' });
          await buscarListaSetores();
        }
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao excluir setor', { variant: 'error' });
    } finally {
      setLoading(false);
      setOpenConfirmDelete(false);
      setSetorId('');
    }
  };

  return (
    <div className={SETORES_COMPONENT_DESIGN.page.container}>
      {/* Header */}
      <div className={SETORES_COMPONENT_DESIGN.page.header.container}>
        <div>
          <h1 className={SETORES_COMPONENT_DESIGN.page.header.title}>Setores</h1>
          <p className={SETORES_COMPONENT_DESIGN.page.header.subtitle}>
            Gerencie os setores e departamentos da sua organização.
          </p>
        </div>
        <button
          onClick={() => handleOpenDialog('Criar')}
          className={SETORES_COMPONENT_DESIGN.page.actionButton}
        >
          <Plus size={18} />
          <span>Novo Setor</span>
        </button>
      </div>

      {/* List */}
      <div className="flex flex-col gap-4">
        {listaSetores.length === 0 ? (
          <div className={SETORES_COMPONENT_DESIGN.emptyState.container}>
            <div className={SETORES_COMPONENT_DESIGN.emptyState.iconBox}>
              <Layers size={32} />
            </div>
            <h3 className={SETORES_COMPONENT_DESIGN.emptyState.title}>Nenhum setor encontrado</h3>
            <p className={SETORES_COMPONENT_DESIGN.emptyState.description}>
              Crie setores para organizar seus atendimentos e usuários.
            </p>
            <button
              onClick={() => handleOpenDialog('Criar')}
              className={SETORES_COMPONENT_DESIGN.emptyState.button}
            >
              Criar meu primeiro setor
            </button>
          </div>
        ) : (
          <GenericTable headLabels={TABLE_HEAD}>
            {listaSetores.map((setor) => (
              <div
                key={setor._id}
                className={GENERIC_TABLE_DESIGN.row.container}
              >
                {/* Nome */}
                <div className={GENERIC_TABLE_DESIGN.common.colMain}>
                  <div className="flex items-center gap-3">
                    <div className={GENERIC_TABLE_DESIGN.common.iconBox}>
                      <Layers size={20} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{setor.nome_setor}</h3>
                      {/* Optional: Add creation date if available */}
                      {/* <span className="text-[10px] text-gray-400">Criado em ...</span> */}
                    </div>
                  </div>
                </div>

                {/* Descrição */}
                <div className="flex-1 hidden md:block px-4">
                  <span className="text-sm text-gray-500 truncate block max-w-sm">
                    {setor.description || '-'}
                  </span>
                </div>

                {/* Ações */}
                <div className="w-24 flex justify-end gap-2">
                  <Tooltip title="Editar">
                    <button
                      onClick={() => handleOpenDialog('Editar', setor)}
                      className={GENERIC_TABLE_DESIGN.common.actionButton}
                    >
                      <Edit2 size={18} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <button
                      onClick={() => handleDeleteClick(setor._id)}
                      className={GENERIC_TABLE_DESIGN.common.deleteButton}
                    >
                      <Trash2 size={18} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ))}
          </GenericTable>
        )}
      </div>

      {/* Dialog Create/Edit */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: SETORES_COMPONENT_DESIGN.dialog.paper }}
      >
        <div className={SETORES_COMPONENT_DESIGN.dialog.container}>
          <DialogTitle sx={SETORES_COMPONENT_DESIGN.dialog.header.sx}>
            {action === 'Criar' ? 'Novo Setor' : 'Editar Setor'}
          </DialogTitle>
          <DialogContent sx={SETORES_COMPONENT_DESIGN.dialog.content.sx}>
            <div className={SETORES_COMPONENT_DESIGN.dialog.content.wrapper}>
              <div>
                <label className={SETORES_COMPONENT_DESIGN.dialog.content.section.label}>
                  Nome do Setor
                </label>
                <TextField
                  fullWidth
                  placeholder="Ex: Comercial, Suporte..."
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Layers size={18} className={SETORES_COMPONENT_DESIGN.dialog.content.section.input.startAdornment} />
                      </InputAdornment>
                    ),
                    sx: SETORES_COMPONENT_DESIGN.dialog.content.section.input.sx
                  }}
                />
              </div>

              <div>
                <label className={SETORES_COMPONENT_DESIGN.dialog.content.section.label}>
                  Descrição
                </label>
                <TextField
                  fullWidth
                  placeholder="Breve descrição do setor"
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  variant="outlined"
                  multiline
                  rows={3}
                  InputProps={{
                    sx: {
                      ...SETORES_COMPONENT_DESIGN.dialog.content.section.input.sx,
                      padding: '12px'
                    }
                  }}
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions sx={SETORES_COMPONENT_DESIGN.dialog.actions.sx}>
            <button
              onClick={handleCloseDialog}
              className={SETORES_COMPONENT_DESIGN.dialog.actions.cancelButton}
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!nome || loading}
              className={`
                                ${SETORES_COMPONENT_DESIGN.dialog.actions.confirmButton.base}
                                ${!nome || loading
                  ? SETORES_COMPONENT_DESIGN.dialog.actions.confirmButton.disabled
                  : SETORES_COMPONENT_DESIGN.dialog.actions.confirmButton.active}
                            `}
            >
              {action === 'Criar' ? 'Criar Setor' : 'Salvar Alterações'}
            </button>
          </DialogActions>
        </div>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <ModalConfimacao
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        loadingFinalizar={loading}
        mensagem={'Deseja mesmo excluir esse setor? Usuários vinculados a ele podem ser afetados.'}
      />
    </div>
  );
}

